import { cva } from "class-variance-authority";

/**
 * Floating chrome shell. `contextual` matches showcase/product-mode.css
 * `.toolbar` (the selection bars, the controls bar). `primary` is the
 * commanding bar's matted housing (exp-canvas-toolbar `.xtb`): an n-05 shell
 * around n-00 group wells, 2px inset, 4px between groups, radius 12 over the
 * tiles' 8. Dark lifts the mat two rungs off the raised canvas ground
 * (n-30 shell, n-40 hairline) so the wells read as cuts in it.
 * `overflow-visible` lets an end tile's focus ring paint past the shell.
 * Vertical is the fit shed's expanded column: the same housing turned 90°,
 * reversed so the first group sits at the foot and the rest rise from it.
 */
export const canvasToolbarShellVariants = cva(
  ["inline-flex flex-nowrap items-center shadow-elev-5"],
  {
    variants: {
      orientation: {
        horizontal: "flex-row",
        vertical: "flex-col-reverse",
      },
      register: {
        contextual: "bg-n-00 border border-n-10 rounded-lg p-1 gap-0.5",
        primary: [
          "relative w-max max-w-full flex-none overflow-visible",
          "bg-n-05 border border-n-10 rounded-xl p-0.5 gap-1",
          "dark:bg-n-30 dark:border-n-40",
        ],
      },
    },
    defaultVariants: {
      orientation: "horizontal",
      register: "contextual",
    },
  },
);

/**
 * The matted bar's mount: a positioned box that IS the bar's box, so the tray
 * and the cursor popover centre on the bar and pin to its top edge with no
 * measurement. It isolates so the bar's z-rung never leaks into the page.
 */
export const canvasToolbarMountClassName =
  "relative isolate inline-flex w-max max-w-full flex-none";

export const canvasToolbarGroupVariants = cva(
  "flex flex-nowrap items-center gap-0.5 shrink-0",
  {
    variants: {
      orientation: {
        horizontal: "flex-row",
        vertical: "flex-col-reverse",
      },
      /** The matted bar's group well: n-00 at the tile's own radius, no
       * padding, so a one-tile group is a 40px square. */
      well: {
        true: "bg-n-00 rounded-lg p-0",
        false: "",
      },
    },
    defaultVariants: {
      orientation: "horizontal",
      well: false,
    },
  },
);

/** The contextual bars' hairline between groups; the matted bar has none. */
export const canvasToolbarDividerVariants = cva(
  "bg-n-10 shrink-0 w-px mx-1 self-stretch my-1",
);

/** Icon / tool button — matches `.toolbar-btn`. */
export const canvasToolbarButtonVariants = cva(
  [
    "group relative inline-flex items-center justify-center gap-0.5",
    "h-8 min-w-8 px-1.5",
    "rounded-sm border-0 bg-transparent",
    "text-n-90 cursor-pointer outline-none",
    "transition-[background-color,color,opacity] duration-150 ease-out",
    "hover:duration-100",
    "hover:bg-n-10",
    "active:duration-0 active:opacity-92",
    "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-2",
    "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
    "disabled:hover:bg-transparent",
    "aria-expanded:bg-n-10",
  ],
  {
    variants: {
      active: {
        true: [
          "bg-[color-mix(in_oklab,var(--rust)_12%,transparent)] text-rust",
          "hover:bg-[color-mix(in_oklab,var(--rust)_18%,transparent)]",
        ],
        false: "",
      },
      destructive: {
        true: "text-lc-blocked hover:bg-lc-blocked/10",
        false: "",
      },
      accent: {
        true: "text-plum hover:bg-plum/10",
        false: "",
      },
      hasDropdown: {
        true: "pr-1.5",
        false: "",
      },
    },
    defaultVariants: {
      active: false,
      destructive: false,
      accent: false,
      hasDropdown: false,
    },
  },
);

/**
 * The commanding bar's tile — exp-canvas-toolbar `.xtb-tile`. A fixed 40px
 * square (invariant to icon presence by construction), radius 8, glyph 20 at
 * stroke 1.75. Hover is acknowledgment: background and colour only, fast in,
 * base out. Selected wears the rust-tint register with one input: a tile that
 * owns a chromatic pick sets `--xtb-sel-ink` inline (CanvasToolbarButton's
 * `ink`), and the wash, the glyph ink and the hover step follow it; every
 * other tile leaves it unset and inherits rust. The color-mix is written on
 * the tile, not the bar, because var() substitutes on the element that
 * declares the property. Open-but-not-selected wears the hover dress; open
 * and selected holds the 18% step.
 */
export const canvasToolbarTileVariants = cva([
  "relative inline-flex shrink-0 items-center justify-center",
  "size-10 min-w-10 p-0",
  "rounded-lg border-0 bg-transparent",
  "text-n-70 cursor-pointer outline-none",
  "transition-[background-color,color,opacity] duration-150 ease-out",
  "hover:duration-100 hover:bg-n-10 hover:text-n-95",
  "active:duration-0 active:opacity-92",
  "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
  "aria-pressed:bg-[color-mix(in_oklab,var(--xtb-sel-ink,var(--rust))_12%,transparent)]",
  "aria-pressed:text-[var(--xtb-sel-ink,var(--rust))]",
  "aria-pressed:hover:bg-[color-mix(in_oklab,var(--xtb-sel-ink,var(--rust))_18%,transparent)]",
  "aria-pressed:aria-expanded:bg-[color-mix(in_oklab,var(--xtb-sel-ink,var(--rust))_18%,transparent)]",
  "not-aria-pressed:aria-expanded:bg-n-10 not-aria-pressed:aria-expanded:text-n-95",
]);

export const canvasToolbarDisplayVariants = cva(
  "inline-flex items-center justify-center h-7 min-w-8 px-0.5 font-mono text-xs leading-none text-n-70",
);

export const canvasToolbarButtonTextVariants = cva(
  "inline-flex items-center px-1 font-sans text-sm leading-none text-n-90 whitespace-nowrap",
  {
    variants: {
      mono: {
        true: "font-mono text-xs",
        false: "",
      },
    },
    defaultVariants: {
      mono: false,
    },
  },
);

/** Matches showcase `.toolbar-color-swatch` — 14px circle, stroke ring. */
export const canvasToolbarColorSwatchClassName =
  "size-3.5 shrink-0 rounded-full border border-n-90";

/** Matches showcase `.toolbar-color-swatch--none`. */
export const canvasToolbarColorSwatchNoneClassName =
  "size-3.5 shrink-0 rounded-full border border-n-30 bg-n-00 bg-[linear-gradient(135deg,transparent_calc(50%-1px),color-mix(in_oklab,var(--rust)_75%,transparent)_calc(50%-1px),color-mix(in_oklab,var(--rust)_75%,transparent)_calc(50%+1px),transparent_calc(50%+1px))]";

/** List-style submenu panel — matches Type-1 toolbar shell chrome. */
export const canvasToolbarSubmenuPanelClassName =
  "min-w-40 rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5";

/** Radix dropdown panel on a canvas toolbar — same chrome as `.toolbar` shell. */
export const canvasToolbarDropdownContentClassName =
  "rounded-lg border border-n-10 bg-n-00 p-1 shadow-elev-5 text-n-90";

/** Row inside a list-style toolbar submenu. */
export const canvasToolbarSubmenuRowClassName =
  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs hover:bg-n-10";

export const canvasToolbarSubmenuRowActiveClassName =
  "bg-[color-mix(in_oklab,var(--rust)_12%,transparent)] text-rust font-medium";

/** Plum accent for entity/process toolbar actions (outside the canvas-tool spec). */
export const canvasToolbarAccentButtonVariants = cva([
  "relative inline-flex items-center justify-center",
  "h-8 min-w-8 px-1.5",
  "rounded-sm border-0 bg-transparent",
  "text-plum cursor-pointer outline-none",
  "transition-[background-color,color,opacity] duration-150 ease-out",
  "hover:duration-100 hover:bg-plum/10",
  "active:duration-0 active:opacity-92",
  "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-2",
  "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
]);
